import React from 'react';
import useSWRMutation from 'swr/mutation';
import mixpanel from 'mixpanel-browser';

import { Alert, Card, Cluster, InputGroup, Input } from '@nearst/ui';
import ClassificationsSelect from './fields/ClassificationsSelect';
import PriceMarkupInput from './fields/PriceMarkupInput';
import MenuCategoriesContainer from './fields/MenuCategoriesContainer';
import { useChannel, useChannelDefinitions } from '@services/channels';
import { customiseProduct } from '@services/stock';
import { useCheckPermission } from '@services/auth/hooks';

import styles from './DeliveryChannelsCard.module.scss';

const useDeliveryChannels = (shopId) => {
	const { data: channelDefinitions } = useChannelDefinitions();
	const { data: uberEats } = useChannel('uber-eats', shopId);
	const { data: deliveroo } = useChannel('deliveroo', shopId);
	const { data: justEat } = useChannel('just-eat', shopId);

	const deliveryChannels = [];
	if (uberEats?.status === 'enabled') {
		deliveryChannels.push({
			...channelDefinitions['uber-eats'],
			config: uberEats.config
		});
	}
	if (deliveroo?.status === 'enabled') {
		deliveryChannels.push({
			...channelDefinitions['deliveroo'],
			config: deliveroo.config
		});
	}
	if (justEat?.status === 'enabled') {
		deliveryChannels.push({
			...channelDefinitions['just-eat'],
			config: justEat.config
		});
	}

	return deliveryChannels;
};

const DeliveryChannelsCard = ({ stockItem, shopId }) => {
	const authorized = useCheckPermission('org:inventory:edit');
	const { trigger, isMutating } = useSWRMutation(`stock_data-${shopId}-${stockItem.barcode}`, async (_, { arg: { field, value } }) =>
		customiseProduct(stockItem.barcode, shopId, { [field]: value })
	);

	const enabledDeliveryChannels = useDeliveryChannels(shopId);

	// Don't display form if no delivery channels are enabled for this shop
	if (!enabledDeliveryChannels?.length) return null;

	const updateField = async (field, target, numeric = false) => {
		let { value } = target;

		if (numeric) {
			if (value === '') return;
			value = Number(target.value);
			if (isNaN(value)) return;
			if (value < 0) {
				value = 0;
				target.value = value; // reset input to 0 as well
			}
		}

		if (value === stockItem[field]) return;

		mixpanel.track('Product delivery attribute changed', { Field: field, Value: value });

		return trigger({ field, value }, { optimisticData: () => ({ ...stockItem, [field]: target.value }), rollbackOnError: true });
	};

	const description = `Edit properties synced to ${enabledDeliveryChannels.map((channel) => channel.title).join(' and ')}.`;

	if (!shopId) {
		return (
			<Card data-cy="delivery-attributes" title="Delivery channels" description={description}>
				<Alert variant="info" className={styles.alert}>
					Switch to an individual location to edit delivery channel details.
				</Alert>
			</Card>
		);
	}

	return (
		<Card data-cy="delivery-attributes" title="Delivery channels" description={description}>
			<Cluster space="2rem">
				{enabledDeliveryChannels.map((channel) => (
					<InputGroup
						key={channel.id}
						label={`${channel.title} price markup`}
						className={styles.markupField}
						htmlFor={`deliveryMarkup.${channel.id}`}
					>
						<PriceMarkupInput
							id={`deliveryMarkup.${channel.id}`}
							channel={channel}
							stockItem={stockItem}
							disabled={!authorized || isMutating}
							currency={stockItem.currency}
							onChange={(value) =>
								updateField('deliveryMarkup', { value: { ...(stockItem.deliveryMarkup || {}), [channel.id]: value } })
							}
						/>
					</InputGroup>
				))}
			</Cluster>
			<InputGroup label="Tax rate" htmlFor="taxPercentage" className={styles.markupField}>
				<Input
					id="taxPercentage"
					suffix="%"
					type="number"
					min="0"
					max="100"
					disabled={!authorized || isMutating}
					defaultValue={stockItem.taxPercentage || enabledDeliveryChannels[0].config.defaultTaxPercentage || ''}
					onBlur={({ target }) => updateField('taxPercentage', target, true)}
				/>
			</InputGroup>
			<InputGroup label="Menu categories">
				<MenuCategoriesContainer onChange={(value) => updateField('deliveryCategories', { value })} />
			</InputGroup>
			<InputGroup label="Classifications">
				<ClassificationsSelect
					defaultValue={stockItem.classifications}
					onChange={(value) => updateField('classifications', { value })}
					disabled={!authorized}
				/>
			</InputGroup>
		</Card>
	);
};

export default DeliveryChannelsCard;
